/** JCS (RFC 8785) canonicalization for envelope hashing and signing.
 *
 * Constraints for Sigil envelopes:
 *   - Root must be a plain object.
 *   - Numbers in Sigil envelopes are always integers (v=1, byte_length,
 *     stamp_id_salt). The number formatter below assumes that; non-integer
 *     numbers throw loudly to surface any future schema addition.
 */

const enc = new TextEncoder();

function canonicalize(value: unknown): string {
  if (value === null) return "null";
  if (value === true) return "true";
  if (value === false) return "false";
  if (typeof value === "number") return canonicalizeNumber(value);
  if (typeof value === "string") return canonicalizeString(value);
  if (Array.isArray(value)) {
    return `[${value.map(canonicalize).join(",")}]`;
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const parts = keys.map((k) => `${canonicalizeString(k)}:${canonicalize(obj[k])}`);
    return `{${parts.join(",")}}`;
  }
  throw new TypeError(`unsupported value in JCS canonicalization: ${typeof value}`);
}

function canonicalizeNumber(n: number): string {
  if (!Number.isFinite(n)) {
    throw new TypeError(`JCS does not allow non-finite numbers: ${n}`);
  }
  if (Number.isInteger(n)) return String(n);
  throw new TypeError(`non-integer numbers are not yet supported in JCS canonicalization: ${n}`);
}

function canonicalizeString(s: string): string {
  let out = '"';
  for (const ch of s) {
    const cp = ch.codePointAt(0);
    if (cp === undefined) continue;
    if (cp === 0x22) out += '\\"';
    else if (cp === 0x5c) out += "\\\\";
    else if (cp === 0x08) out += "\\b";
    else if (cp === 0x09) out += "\\t";
    else if (cp === 0x0a) out += "\\n";
    else if (cp === 0x0c) out += "\\f";
    else if (cp === 0x0d) out += "\\r";
    else if (cp < 0x20) out += `\\u${cp.toString(16).padStart(4, "0")}`;
    else out += ch;
  }
  out += '"';
  return out;
}

/** Return the JCS-canonical UTF-8 bytes for `obj`. */
export function canonicalBytes(obj: Record<string, unknown>): Uint8Array {
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    throw new TypeError("canonicalBytes requires a plain object root");
  }
  return enc.encode(canonicalize(obj));
}
